import CopyButton from "@components/buttons/CopyButton";
import { GetPHPInstanceData } from "@services/models/Prime/GetPHPInstancesModel";
import { ColDef } from "ag-grid-community";
import React from "react";
import ConstantFunctions from "src/utils/constants_functions";
import { formatAdaptiveDuration } from "../utils/formatAdaptiveDuration";
import {
  ActiveDurationPill,
  ActiveDurationTone,
} from "./PrimeOrderPills";

const constantFunctions = new ConstantFunctions();

export const PHILIPPINES_EVENT_IDS = {
  CREATED: "1",
  INITIATED: "2",
  PAYMENT_PROCESSING: "3",
  COMPLETED: "4",
  FAILED: "8",
  CANCELLED: "9",
  KYC_FAILED: "10",
};

export const PhilippinesAmountCellRenderer = (params: any) => {
  const data = params.data as GetPHPInstanceData;
  return (
    <div className="flex items-center text-[10px] text-text-base font-medium text-gray-700">
      <div className="flex items-center w-[60px]">
        <img
          src={data.cryptoCurrencyImage}
          alt=""
          className="h-[12px] w-[12px] mr-1"
        />
        <span>{data.cryptoAmount?.toFixed(2) ?? "- -"}</span>
      </div>
      <span className="mx-1">|</span>
      <div className="flex items-center ml-[4px]">
        <img
          src={data.fiatCurrencyImage}
          alt=""
          className="h-[12px] w-[12px] mr-1"
        />
        <span>{data.fiatAmount?.toFixed(2) ?? "- -"}</span>
      </div>
    </div>
  );
};

const getEventTimeDiffMs = (
  data: GetPHPInstanceData,
  eventId: string,
): number => {
  if (!data.eventDetails || data.eventDetails.length === 0) {
    return 0;
  }

  const sortedEvents = [...data.eventDetails].sort((a, b) => {
    const timeA = new Date(a.timestamp || data.createdAt).getTime();
    const timeB = new Date(b.timestamp || data.createdAt).getTime();
    return timeA - timeB;
  });

  const eventIndex = sortedEvents.findIndex(
    (e) => e.eventId?.toString() === eventId,
  );
  if (eventIndex === -1) {
    return 0;
  }

  const currentEventTime = new Date(
    sortedEvents[eventIndex].timestamp || data.createdAt,
  ).getTime();
  const prevEventTime =
    eventIndex === 0
      ? new Date(data.createdAt).getTime()
      : new Date(
          sortedEvents[eventIndex - 1].timestamp || data.createdAt,
        ).getTime();

  return Math.abs(currentEventTime - prevEventTime);
};

const getEventStatus = (
  data: GetPHPInstanceData,
  eventId: string,
): React.ReactNode => {
  const eventIndex = data.eventDetails?.findIndex(
    (e) => e.eventId?.toString() === eventId,
  );
  if (eventIndex === -1 || eventIndex === undefined) return null;

  if (eventId === PHILIPPINES_EVENT_IDS.CREATED) {
    return (
      <div className="rounded-[6px] w-[25px] h-[18px] p-[4px] text-[10px] text-text-base flex items-center justify-center bg-bg-button-secondary"></div>
    );
  }

  const { display, tooltip } = formatAdaptiveDuration(
    getEventTimeDiffMs(data, eventId),
  );

  let color = "bg-bg-button-secondary";
  if (eventId === PHILIPPINES_EVENT_IDS.FAILED) {
    color = "bg-bg-status-red";
  } else if (eventId === PHILIPPINES_EVENT_IDS.CANCELLED) {
    color = "bg-bg-status-red";
  } else if (eventId === PHILIPPINES_EVENT_IDS.KYC_FAILED) {
    color = "bg-bg-status-red";
  } else if (eventId === PHILIPPINES_EVENT_IDS.COMPLETED) {
    color = "bg-bg-helper-green-dark";
  }

  return (
    <div
      title={tooltip}
      className={`rounded-[6px] h-[24px] min-w-[48px] px-[4px] py-[4px] text-[10px] text-text-base flex items-center justify-center whitespace-nowrap ${color}`}
    >
      {display}
    </div>
  );
};

export const PhilippinesCreatedCellRenderer = (params: any) => {
  return getEventStatus(params.data, PHILIPPINES_EVENT_IDS.CREATED);
};

export const PhilippinesInitiatedCellRenderer = (params: any) => {
  return getEventStatus(params.data, PHILIPPINES_EVENT_IDS.INITIATED);
};

export const PhilippinesPaymentProcessingCellRenderer = (params: any) => {
  return getEventStatus(params.data, PHILIPPINES_EVENT_IDS.PAYMENT_PROCESSING);
};

export const PhilippinesCompletedCellRenderer = (params: any) => {
  return getEventStatus(params.data, PHILIPPINES_EVENT_IDS.COMPLETED);
};

export const PhilippinesFailedCellRenderer = (params: any) => {
  return getEventStatus(params.data, PHILIPPINES_EVENT_IDS.FAILED);
};

export const PhilippinesCancelledCellRenderer = (params: any) => {
  return getEventStatus(params.data, PHILIPPINES_EVENT_IDS.CANCELLED);
};

export const PhilippinesKycFailedCellRenderer = (params: any) => {
  return getEventStatus(params.data, PHILIPPINES_EVENT_IDS.KYC_FAILED);
};

export const PhilippinesActiveCellRenderer = (params: any) => {
  const data = params.data as GetPHPInstanceData;

  const durationFromMs = (diffMs: number, tone: ActiveDurationTone) => (
    <ActiveDurationPill diffMs={diffMs} tone={tone} />
  );

  const sortedEventDetails = [...(data.eventDetails || [])].sort((a, b) => {
    return (
      new Date(b.timestamp || data.createdAt).getTime() -
      new Date(a.timestamp || data.createdAt).getTime()
    );
  });

  if (data.eventDetails && data.eventDetails.length > 0) {
    const latestEvent = sortedEventDetails[0];
    const showTimeDiffIds = [4, 8, 9, 10];
    const hasShowTimeDiffEvent = sortedEventDetails.some(
      (e) => e.eventId != null && showTimeDiffIds.includes(e.eventId),
    );
    if (hasShowTimeDiffEvent) {
      const latestEventId = Number(latestEvent.eventId);
      const isFailed = [8, 9, 10].includes(latestEventId);
      const isCompleted = latestEventId === 4;

      return durationFromMs(
        new Date(latestEvent.timestamp || data.createdAt).getTime() -
          new Date(data.createdAt).getTime(),
        isFailed ? "failed" : isCompleted ? "completed" : "active",
      );
    }
  }

  return durationFromMs(
    Date.now() - new Date(data.createdAt).getTime(),
    "active",
  );
};

export const createPhilippinesOrderColumnDefs = (): ColDef[] => [
  {
    headerName: "Date",
    field: "createdAt",
    flex: 0.9,
    minWidth: 90,
    maxWidth: 130,
    cellRenderer: (params: any) => {
      return (
        <span className="text-[10px] text-text-base font-medium">
          {constantFunctions.dateFormatInDDMMYY(params.data.createdAt)}
        </span>
      );
    },
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: "agDateColumnFilter",
  },
  {
    headerName: "Instance ID",
    field: "instanceId",
    flex: 1.1,
    minWidth: 150,
    maxWidth: 160,
    cellRenderer: (params: any) => {
      const { data } = params;
      return (
        <div className="flex items-start h-full gap-1">
          <span
            className={`text-[10px] font-medium px-[6px] h-[24px] rounded flex items-center mr-1 ${
              data.isBuying === 1 ? "bg-bg-status-green" : "bg-bg-status-red"
            } text-text-base`}
          >
            {data.isBuying === 1 ? "B" : "S"}
          </span>

          <div className="flex flex-col leading-[10px] flex-1">
            {data.instanceId ? (
              Array.from(
                {
                  length: Math.min(
                    Math.ceil(data.instanceId.toString().length / 12),
                    3,
                  ),
                },
                (_, index) => (
                  <span
                    key={index}
                    className="text-[10px] text-text-base font-medium"
                  >
                    {data.instanceId
                      .toString()
                      .slice(index * 12, (index + 1) * 12)}
                  </span>
                ),
              )
            ) : (
              <span className="text-[10px] text-text-base font-medium">
                - -
              </span>
            )}
          </div>

          {data.instanceId && (
            <CopyButton
              copyText={data.instanceId.toString()}
              copyMessage="Instance ID copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          )}
        </div>
      );
    },
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Merchant",
    field: "merchantName",
    flex: 1.2,
    minWidth: 120,
    maxWidth: 180,
    cellRenderer: (params: any) => {
      return (
        <span className="text-[10px] text-text-base font-medium">
          {params.data.merchantName || "- -"}
        </span>
      );
    },
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: "agTextColumnFilter",
    wrapText: true,
    autoHeight: true,
    cellStyle: {
      whiteSpace: "normal",
      wordBreak: "break-word",
      lineHeight: "1.2",
    },
  },
  {
    headerName: "Merchant Ref ID",
    field: "merchantRefId",
    flex: 1.2,
    minWidth: 150,
    maxWidth: 180,
    cellRenderer: (params: any) => {
      const merchantRefId = params.data.merchantRefId;
      return (
        <div className="flex items-center justify-between h-full gap-1">
          <div className="flex flex-col leading-[10px] flex-1">
            {merchantRefId ? (
              Array.from(
                {
                  length: Math.min(
                    Math.ceil(merchantRefId.toString().length / 12),
                    3,
                  ),
                },
                (_, index) => (
                  <span
                    key={index}
                    className="text-[10px] text-text-base font-medium"
                  >
                    {merchantRefId
                      .toString()
                      .slice(index * 12, (index + 1) * 12)}
                  </span>
                ),
              )
            ) : (
              <span className="text-[10px] text-text-base font-medium">
                - -
              </span>
            )}
          </div>

          {merchantRefId && (
            <CopyButton
              copyText={merchantRefId.toString()}
              copyMessage="Merchant Ref ID copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          )}
        </div>
      );
    },
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Amount",
    field: "amount",
    flex: 1.8,
    minWidth: 180,
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: false,
    sortable: false,
    cellRenderer: PhilippinesAmountCellRenderer,
  },
  {
    headerName: "Created",
    field: "created",
    flex: 0.9,
    minWidth: 90,
    maxWidth: 120,
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: false,
    sortable: false,
    cellRenderer: PhilippinesCreatedCellRenderer,
    cellStyle: () => ({
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }),
  },
  {
    headerName: "Initiated",
    field: "initiated",
    flex: 0.9,
    minWidth: 90,
    maxWidth: 120,
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: false,
    sortable: false,
    cellRenderer: PhilippinesInitiatedCellRenderer,
    cellStyle: () => ({
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }),
  },
  {
    headerName: "Pay Proc",
    field: "paymentProcessing",
    flex: 0.9,
    minWidth: 90,
    maxWidth: 120,
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: false,
    sortable: false,
    cellRenderer: PhilippinesPaymentProcessingCellRenderer,
    cellStyle: () => ({
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }),
  },
  {
    headerName: "Completed",
    field: "completed",
    flex: 0.9,
    minWidth: 90,
    maxWidth: 120,
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: false,
    sortable: false,
    cellRenderer: PhilippinesCompletedCellRenderer,
    cellStyle: () => ({
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }),
  },
  {
    headerName: "Failed",
    field: "failed",
    flex: 0.8,
    minWidth: 80,
    maxWidth: 110,
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: false,
    sortable: false,
    cellRenderer: PhilippinesFailedCellRenderer,
    cellStyle: () => ({
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }),
  },
  {
    headerName: "Cancelled",
    field: "cancelled",
    flex: 0.9,
    minWidth: 90,
    maxWidth: 120,
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: false,
    sortable: false,
    cellRenderer: PhilippinesCancelledCellRenderer,
    cellStyle: () => ({
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }),
  },
  {
    headerName: "KYC Failed",
    field: "kycFailed",
    flex: 1,
    minWidth: 100,
    maxWidth: 140,
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: false,
    sortable: false,
    cellRenderer: PhilippinesKycFailedCellRenderer,
    cellStyle: () => ({
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }),
  },
  {
    headerName: "Active",
    field: "active",
    flex: 0.8,
    minWidth: 80,
    maxWidth: 110,
    headerClass: "ag-header-cell-custom",
    cellClass: "ag-cell-custom",
    filter: false,
    sortable: false,
    cellRenderer: PhilippinesActiveCellRenderer,
    cellStyle: () => ({
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }),
  },
];
